/**
 * UserProfileContext - Single source of truth for current user profile (avatarId, favoriteLeagueIds).
 * Fetches GET /users/profile/:userId once so AvatarContext and useFavoriteLeagues do not duplicate the request.
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSupabaseAuth } from './SupabaseAuthContext';
import { useApiClient } from '../../../helpers/apiClient.helper';
import { API_ENDPOINTS } from '../../../constants';
import { normalizeAvatarId, DEFAULT_AVATAR_ID, type AvatarId } from '../../../constants/avatarIds';
import type { EquippedMap } from '../../../components/avatars/LayeredAvatarLab/characterCustomizer.types';
import { useEconomyStore } from '../stores/economyStore';
import { parseArenaStatsFromProfileResponse } from '../../challenges/helpers/parseArenaStatsFromProfileResponse.helper';
import { useProfileForegroundRefresh } from '../hooks/useProfileForegroundRefresh';
import type { ArenaStats } from '../../challenges/types/arenaStats.types';

export interface UserProfileData {
  avatarId: AvatarId;
  /** Custom layered look; non-null overrides avatarId everywhere it renders. */
  avatarConfig: EquippedMap | null;
  favoriteLeagueIds: string[];
  coins: number;
  /** All-time-high coin balance — drives the coin-based rank badge (specs/coin-based-ranks). */
  peakCoins: number;
  dailyClaimStreak: number;
  lastDailyClaimAt: string | null;
  lastSeenResultsAt: string | null;
  arenaStats: ArenaStats | null;
  /** App username from API (used for account deletion confirmation phrase). */
  username: string | null;
}

interface UserProfileContextValue {
  profile: UserProfileData | null;
  isLoading: boolean;
  error: Error | null;
  updateProfile: (partial: Partial<UserProfileData>) => void;
  /** Re-fetch profile from server (e.g. when returning to a screen). Only updates state if data changed. */
  refetchProfile: () => Promise<void>;
}

const UserProfileContext = createContext<UserProfileContextValue | undefined>(undefined);

function parseProfileFromResponse(data: unknown): UserProfileData | null {
  const profile = (data as { success?: boolean; data?: { profile?: unknown } })?.data?.profile;
  if (!profile || typeof profile !== 'object') return null;

  const p = profile as {
    avatarId?: unknown;
    avatarConfig?: unknown;
    favoriteLeagueIds?: unknown;
    coins?: unknown;
    peakCoins?: unknown;
    dailyClaimStreak?: unknown;
    lastDailyClaimAt?: unknown;
    username?: unknown;
  };
  const username =
    typeof p.username === 'string' && p.username.trim().length > 0 ? p.username.trim() : null;
  const avatarIdRaw = p.avatarId;
  const avatarId: AvatarId =
    typeof avatarIdRaw === 'string' ? normalizeAvatarId(avatarIdRaw) : DEFAULT_AVATAR_ID;
  // Server validates the config on write; trust a non-null object here. Unknown item ids
  // (e.g. an item later removed from the catalog) are dropped at render time, not here.
  const avatarConfig: EquippedMap | null =
    p.avatarConfig && typeof p.avatarConfig === 'object' && !Array.isArray(p.avatarConfig)
      ? (p.avatarConfig as EquippedMap)
      : null;

  const rawIds = p.favoriteLeagueIds;
  const favoriteLeagueIds = Array.isArray(rawIds)
    ? (rawIds as unknown[]).filter((id): id is string => typeof id === 'string')
    : [];

  const coins = typeof p.coins === 'number' ? p.coins : 0;
  // Fall back to current coins if an older server omits peakCoins (never below the balance).
  const peakCoins = typeof p.peakCoins === 'number' ? Math.max(p.peakCoins, coins) : coins;
  const dailyClaimStreak = typeof p.dailyClaimStreak === 'number' ? p.dailyClaimStreak : 0;
  const lastDailyClaimAt = typeof p.lastDailyClaimAt === 'string' ? p.lastDailyClaimAt : null;
  const lastSeenResultsAt = typeof (p as { lastSeenResultsAt?: unknown }).lastSeenResultsAt === 'string'
    ? (p as { lastSeenResultsAt: string }).lastSeenResultsAt
    : null;

  const arenaStats = parseArenaStatsFromProfileResponse(data);

  return {
    avatarId,
    avatarConfig,
    favoriteLeagueIds,
    coins,
    peakCoins,
    dailyClaimStreak,
    lastDailyClaimAt,
    lastSeenResultsAt,
    arenaStats,
    username,
  };
}

/** Loaded-but-empty profile: consumers treat profile === null as "not yet loaded", profile object as "load finished". */
const EMPTY_PROFILE: UserProfileData = {
  avatarId: DEFAULT_AVATAR_ID,
  avatarConfig: null,
  favoriteLeagueIds: [],
  coins: 0,
  peakCoins: 0,
  dailyClaimStreak: 0,
  lastDailyClaimAt: null,
  lastSeenResultsAt: null,
  arenaStats: null,
  username: null,
};

interface UserProfileProviderProps {
  children: React.ReactNode;
}

export const UserProfileProvider: React.FC<UserProfileProviderProps> = ({ children }) => {
  const { isLoaded, isSignedIn, userId } = useSupabaseAuth();
  const apiClient = useApiClient();
  const apiClientRef = useRef(apiClient);
  const [profile, setProfile] = useState<UserProfileData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    apiClientRef.current = apiClient;
  }, [apiClient]);

  useEffect(() => {
    if (!isLoaded) return;
    if (!isSignedIn || !userId) {
      setProfile(null);
      setIsLoading(false);
      setError(null);
      useEconomyStore.getState().resetEconomy();
      return;
    }

    const endpoint = `${API_ENDPOINTS.users.profile}/${userId}`;
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    const client = apiClientRef.current;
    if (!client || typeof client !== 'function') {
      setProfile(EMPTY_PROFILE);
      setIsLoading(false);
      return;
    }

    client(endpoint)
      .then((response: { status: number; data: unknown }) => {
        if (cancelled) return;
        if (response.status >= 400) {
          setProfile(EMPTY_PROFILE);
          return;
        }
        const parsed = parseProfileFromResponse(response.data);
        const result = parsed ?? EMPTY_PROFILE;
        setProfile(result);
        // Peak BEFORE coins: useSelfRank is gated on coinsLoaded (set by syncCoins), so the first
        // rank it ever observes already reflects the true peak — no transient rank-1 → false
        // Badge Forge on app open for a spent-down user (specs/coin-based-ranks).
        useEconomyStore.getState().syncPeakCoins(result.peakCoins);
        useEconomyStore.getState().syncCoins(result.coins);
        useEconomyStore.getState().syncClaimState(result.dailyClaimStreak, result.lastDailyClaimAt);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error(String(err)));
        setProfile(EMPTY_PROFILE);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isLoaded, isSignedIn, userId]);

  const updateProfile = useCallback((partial: Partial<UserProfileData>) => {
    setProfile((prev) => {
      if (!prev) return prev;
      return { ...prev, ...partial };
    });
  }, []);

  const refetchProfile = useCallback(async () => {
    if (!isSignedIn || !userId) return;
    const client = apiClientRef.current;
    if (!client || typeof client !== 'function') return;
    const endpoint = `${API_ENDPOINTS.users.profile}/${userId}`;
    try {
      const response = await client(endpoint) as { status: number; data: unknown };
      if (response.status >= 400) return;
      const parsed = parseProfileFromResponse(response.data);
      const next = parsed ?? EMPTY_PROFILE;
      // A successful refetch must clear any prior load error, otherwise the arena-stats
      // banner stays stuck in its error state even after the retry repopulates the data.
      setError(null);
      // Peak before coins (see the initial-fetch note): avoids a transient low rank on refetch.
      useEconomyStore.getState().syncPeakCoins(next.peakCoins);
      useEconomyStore.getState().syncCoins(next.coins);
      useEconomyStore.getState().syncClaimState(next.dailyClaimStreak, next.lastDailyClaimAt);
      setProfile((prev) => {
        if (!prev) return next;
        if (
          prev.avatarId === next.avatarId &&
          JSON.stringify(prev.avatarConfig) === JSON.stringify(next.avatarConfig) &&
          prev.coins === next.coins &&
          prev.dailyClaimStreak === next.dailyClaimStreak &&
          prev.lastDailyClaimAt === next.lastDailyClaimAt &&
          prev.lastSeenResultsAt === next.lastSeenResultsAt &&
          prev.username === next.username &&
          JSON.stringify(prev.arenaStats) === JSON.stringify(next.arenaStats) &&
          prev.favoriteLeagueIds.length === next.favoriteLeagueIds.length &&
          prev.favoriteLeagueIds.every((id, i) => id === next.favoriteLeagueIds[i])
        ) {
          return prev;
        }
        return next;
      });
    } catch {
      // Silently fail; keep current profile
    }
  }, [isSignedIn, userId]);

  useProfileForegroundRefresh(refetchProfile, isLoaded && isSignedIn && !!userId);

  const value = useMemo<UserProfileContextValue>(
    () => ({
      profile,
      isLoading,
      error,
      updateProfile,
      refetchProfile,
    }),
    [profile, isLoading, error, updateProfile, refetchProfile]
  );

  return (
    <UserProfileContext.Provider value={value}>{children}</UserProfileContext.Provider>
  );
};

export function useUserProfile(): UserProfileContextValue {
  const context = useContext(UserProfileContext);
  if (context === undefined) {
    throw new Error('useUserProfile must be used within UserProfileProvider');
  }
  return context;
}

/**
 * Non-throwing variant: returns undefined when there is no provider (e.g. Storybook).
 * Use in shared/leaf components that may render outside the app provider tree.
 */
export function useUserProfileOptional(): UserProfileContextValue | undefined {
  return useContext(UserProfileContext);
}
